import React, { useState } from "react";
import MtaStsValidationResultTable, { IMtaStsValidationTableItem } from "./MtaStsValidationResultTable";
import { IRcaMtaStsValidationPolicyDetails } from "../../models/IRcaMtaStsValidationPolicyDetails";
import { IRcaMtaStsValidationStatus } from "../../models/IRcaMtaStsValidationStatus";
import { IRcaMtaStsSectionDetails } from "../../models/IRcaMtaStsSectionDetails";
import { CollapsePanel } from "../CollapsePanel";
import { mergeStyleSets, Stack, StackItem } from "@fluentui/react";
import { MtaStsValidationResultIcon } from "./MtaStsValidationResultIcon";
import { ComponentDecorators } from "../ComponentDecorators";

const classNames = mergeStyleSets({
  root: {
    paddingLeft: 30,
    margin: 15,
  },
});

/**
 * Gets the display for the policy collapsible header.
 *
 * @param policy the policy properties
 * @returns the display for policy collapsible header
 */
const getPolicyDisplay = (policy: IRcaMtaStsSectionDetails<IRcaMtaStsValidationPolicyDetails>): React.ReactElement => {
  const shownPolicyValue: string = policy.Value?.PolicyFile?.Value || "n/a";
  return (
    <Stack horizontal verticalAlign="center" horizontalAlign="space-around" tokens={{ childrenGap: 5 }}>
      <StackItem>{WebsiteStrings.MtaStsPolicyLabel}</StackItem>
      <StackItem>{shownPolicyValue}</StackItem>
      <MtaStsValidationResultIcon mtaStsDetails={policy.Details} mtaStsStatus={policy.Status} />
    </Stack>
  );
};

/**
 * The function to map the received policy into table items.
 *
 * @param policy  The received policy to map.
 * @returns The populated table items.
 */
const mapPolicyToMtaStsValidationTableItem = (policy: IRcaMtaStsValidationPolicyDetails): IMtaStsValidationTableItem[] => {
  return [
    {
      Component: "Policy File",
      Value: policy?.PolicyFile?.Value ?? "n/a",
      Status: policy?.PolicyFile?.Status ?? IRcaMtaStsValidationStatus.ConfigurationNotPresent,
      Details: ComponentDecorators.replaceLinks(policy?.PolicyFile?.Details ?? "n/a"),
    },
    {
      Component: "MX Host",
      Value: policy?.MxHost?.Value?.replace(/,/g, "\n") ?? "n/a",
      Status: policy?.MxHost?.Status ?? IRcaMtaStsValidationStatus.ConfigurationNotPresent,
      Details: ComponentDecorators.replaceLinks(policy?.MxHost?.Details ?? "n/a"),
    },
    {
      Component: "Mode",
      Value: policy?.Mode?.Value ?? "n/a",
      Status: policy?.Mode?.Status ?? IRcaMtaStsValidationStatus.ConfigurationNotPresent,
      Details: ComponentDecorators.replaceLinks(policy?.Mode?.Details ?? "n/a"),
    },
    {
      Component: "Max Age",
      Value: policy?.MaxAge?.Value ?? "n/a",
      Status: policy?.MaxAge?.Status ?? IRcaMtaStsValidationStatus.ConfigurationNotPresent,
      Details: ComponentDecorators.replaceLinks(policy?.MaxAge?.Details ?? "n/a"),
    },
    {
      Component: "Raw Policy",
      Value: policy?.RawPolicy?.Value ?? "n/a",
      Status: policy?.RawPolicy?.Status ?? IRcaMtaStsValidationStatus.ConfigurationNotPresent,
      Details: ComponentDecorators.replaceLinks(policy?.RawPolicy?.Details ?? "n/a"),
    },
  ];
};

/**
 * The domain result UI
 *
 * @param props - The component properties
 * @returns The markup
 */
export const MtaStsValidationResultPolicy: React.FC<{
  /**
   * List of items to render in the table
   */
  policy: IRcaMtaStsSectionDetails<IRcaMtaStsValidationPolicyDetails>;
}> = ({ policy }) => {
  const [expanded, setExpanded] = useState(true);

  const tableItems = mapPolicyToMtaStsValidationTableItem(policy.Value);

  return (
    <Stack className={classNames.root}>
      <CollapsePanel
        expanded={expanded}
        renderTitleRow={(): React.ReactElement => getPolicyDisplay(policy)}
        setExpanded={setExpanded}
        styles={{
          content: {
            root: {
              marginLeft: 20,
            },
          },
        }}
      >
        <MtaStsValidationResultTable items={tableItems} />
      </CollapsePanel>
    </Stack>
  );
};
